import React, { forwardRef, useImperativeHandle, useMemo } from "react";
import { useIntl } from "umi";
import classnames from 'classnames';

import { Dropdown } from "tea-component";
import Abnormal from "@home/components/Abnormal";
import SearchWithTags from "@/pages/datalab/components/SearchWithTags";
import OptionsGroup from "@/pages/dashboard/components/FilterSelect/components/OptionsGroup";
import { useDefaultFilterSelectorWithTagProps } from "@/pages/datalab/components/FilterSelectorWithTag/hooks";
import { FilterSelectorWithTagProps, } from "@/pages/datalab/components/FilterSelectorWithTag/type";
import { TagType } from "@/pages/datalab/components/SearchWithTags/type";
import './index.scss';
import { difference } from 'lodash-es';

const FilterSelectorWithTag = (props: FilterSelectorWithTagProps, ref) => {
  const {
    name,// 筛选器的名称
    isFull, // 是否是全选状态
    // 搜索框相关属性
    searchPlaceholder,
    onDeleteTag,
    onSearch,
    autoClearSearchValue = true,
    onSearchKeywordChange,

    // 选项相关
    values, // 勾选的值
    virtual = true, // 是否启动虚拟列表
    listHeight = 300, // 列表高度

    // 选项加载状态
    loading = false,
    error = null,
    failedMsg,
    nodataMsg,

    // 插入的额外组件
    topContentRender,
    beforeOptionsRender,

    // 下拉弹窗
    boxClassName,
    onClose,
    onOpen,
    options,
    dropDownRef,
    ...dropdownProps

  } = props;

  const { formatMessage } = useIntl();

  const { internalTags, internalSearchWord, setInternalSearchWord, internalSearchState, internalOptions, handleValuesChange, isChangeRef } = useDefaultFilterSelectorWithTagProps(props);

  useImperativeHandle(ref, () => ({
    searchWord: internalSearchWord,
    clearSearchWord: () => {
      setInternalSearchWord('');
    },
  }));

  const searchValue = (isFull && internalSearchState) ? [] : values;

  const isShowClear = useMemo(() => {
    if (internalSearchState) {
      return internalOptions.some(opt => searchValue?.includes(opt?.value));
    }
    return searchValue.length > 0;
  }, [internalOptions, values]);

  return (
    <Dropdown
      {...dropdownProps}
      ref={dropDownRef}
      appearance="button"
      clickClose={false}
      // review 强制使用classnames
      boxClassName={classnames('dbl-filter-selector-dropdown-box', boxClassName)}
      onOpen={() => {
        isChangeRef.current = false;
        onOpen?.();
      }}
      onClose={() => {
        autoClearSearchValue && setInternalSearchWord('');
        onClose?.(isChangeRef.current);
      }}
      key={name}
    >
      {(close) => (
        <>
          {/* 外界插入的组件 */}
          {topContentRender?.(close)}

          {/* 搜索框 和 tag展示 (初始化加载并且值是全部的情况下, 不展示tags)*/}
          <SearchWithTags
            searchPlaceholder={searchPlaceholder ?? formatMessage({ id: 'dbl_common_search' })}
            tags={internalTags}
            searchWord={internalSearchWord}
            onDeleteTag={(tag: TagType, index: number) => {
              handleValuesChange({ values: values?.filter(value => value !== tag?.value) ?? [], isFull: false });
              onDeleteTag?.(tag, index);
            }}
            onSearch={onSearch}
            onSearchKeywordChange={(keyword: string) => {
              onSearchKeywordChange?.(keyword);
              setInternalSearchWord(keyword);
            }}
          />

          {/* 全选 清除 搜索结果*/}
          {/* 选项 和 加载状态*/}
          <Abnormal
            style={{
              height: listHeight,
            }}
            isLoading={loading}
            isError={!!error}
            failedMsg={failedMsg}
            isNoData={internalOptions && !internalOptions.length}
            nodataMsg={nodataMsg}
            component={(
              <>
                {beforeOptionsRender?.({
                  close,
                  isFull,
                  values,
                  internalSearchState,
                  handleValuesChange,
                })}
                <div className="dbl-filter-selector-all">
                  {
                    !internalSearchState ? <div className="left-text">{name}</div> : <div className="left-text">{formatMessage({ id: 'dbl_common_search_result' }, { count: internalOptions?.length ?? 0 })}</div>
                  }
                  <div
                    className="right-btn"
                    onClick={() => {
                      // 有搜索值时，全选&取消全选只对当前选项内值有效
                      props?.onToggleAllState?.(!isShowClear);
                      // 点击是对原有状态取反
                      const currentValues = internalOptions.map(opt => opt.value);
                      // 切换全选清除按钮, 是对当前下拉选项进行操作
                      // 所以当前是一个搜索状态时, 全选清除不是对 真正的全部选项进行操作, 而是对搜索结果选项进行操作
                      // 所以 提供给外界的全选状态判断 要单独判断
                      if (internalSearchState) { // 搜索状态下

                        if (isShowClear) { // 清除
                          handleValuesChange({ values: difference(values, currentValues), isFull: false });
                        } else { // 全选
                          handleValuesChange({ values: [...searchValue, ...currentValues], isFull: false });
                        }

                      } else { // 非搜索状态下

                        if (isShowClear) { // 清除
                          handleValuesChange({ values: [], isFull: false });
                        } else { // 全选
                          // 目前只能通过 非搜索状态下的 全选按钮, 选中全选状态.
                          handleValuesChange({ values: currentValues, isFull: true });
                        }

                      }

                    }}
                  >
                    {/* 选项都空才展示全选，其余状态都展示清除 */}
                    {isShowClear ? formatMessage({ id: 'dbl_common_clear_all' }) : formatMessage({ id: 'dbl_common_select_all' })}
                  </div>
                </div>
                <OptionsGroup
                  // value={values}
                  value={searchValue}
                  virtual={virtual}
                  listHeight={listHeight}
                  isMatch={(internalOptions?.length ?? 0) > 0}
                  options={internalOptions || []}
                  formatMessage={() => ''}
                  onChange={(values: string[], context) => {

                    // 情况1: 搜索状态下, 不可能通过勾选 选中所有, 所以isFull 必须是false, 不能是 true
                    // 情况2: 非搜索状态下, 也不能通过勾选 选中所有, 所以isFull 也是false (目前只能通过 非搜索状态下的 全选按钮, 选中全选状态)
                    handleValuesChange({ values, isFull: false }, context);
                  }}
                />

              </>
            )}

          />
        </>
      )}
    </Dropdown>
  );
};

export default forwardRef(FilterSelectorWithTag);